import { api } from "../../services/api";

/**
 * Client HTTP du portail client (`/api/client/*`).
 *
 * Toutes les requêtes nécessitent un token JWT pour un utilisateur `CUSTOMER`
 * dont la fiche `Customer` est liée (géré côté backend par `requireCustomer`).
 */

export interface ClientProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  phoneDialCode: string;
  customer: {
    id: string;
    firstName: string | null;
    lastName: string;
    type: "INDIVIDUAL" | "COMPANY" | "INSTITUTION";
    phone: string | null;
    phoneDialCode: string;
    email: string | null;
    defaultPaymentMode: "EN_COMPTE" | "COMPTANT" | null;
    indice: number | null;
    enterpriseId: string | null;
    enterprise: {
      id: string;
      name: string;
      discountRules: Array<{
        indexMin: number;
        indexMax: number;
        fixedDeliveryPrice: number | null;
      }>;
    } | null;
    allergens: Array<{ id: string; name: string }>;
  };
}

export interface ClientAddress {
  id: string;
  customerId: string;
  label: string | null;
  address: string;
  postalCode: string | null;
  city: string | null;
  zoneId: string | null;
  zone: { id: string; name: string; zoneNumber: number; deliveryCost?: number | null } | null;
  isDefault: boolean;
  isBilling: boolean;
  displayOrder: number;
  latitude: number | null;
  longitude: number | null;
}

export interface PickupLocation {
  name: string;
  address: string;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  phoneDialCode: string;
  latitude: number | null;
  longitude: number | null;
}

export type ClientMenuSlotType =
  | "ENTREE"
  | "PLAT_VIANDE"
  | "PLAT_POISSON"
  | "PLAT_VEGE"
  | "PLAT_AUTRE"
  | "ACCOMPAGNEMENT"
  | "DESSERT"
  | "BOISSON";

export interface ClientMenuProductAvailability {
  estimated: number;
  orderedQuantity: number;
  remainingQuantity: number;
}

export interface ClientMenuSlot {
  id: string;
  slotType: ClientMenuSlotType;
  displayOrder: number;
  /** Plat éligible à la formule Switch ce jour-là (accompagnement remplaçable). */
  isSwitch: boolean;
  product: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    imageUrl: string | null;
    isVegetarian: boolean;
    isPremium: boolean;
    isSpicy: boolean;
    allergens: Array<{ id: string; name: string; code: string; icon: string | null }>;
    availability: ClientMenuProductAvailability | null;
  } | null;
}

export interface ClientMenu {
  date: string;
  weeklyMenuId: string;
  slots: ClientMenuSlot[];
}

export interface ClientOrderItem {
  id: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  notes?: string | null;
  isSwitch?: boolean;
  product: { id: string; name: string; price: number; description?: string };
}

export interface ClientOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  customerAddressId: string | null;
  customerAddress?: {
    id: string;
    address: string;
    postalCode?: string | null;
    city?: string | null;
    zone?: { id: string; name: string };
  } | null;
  orderDate: string;
  deliveryDate: string;
  deliveryTimeSlot: string | null;
  status:
    | "PENDING"
    | "CONFIRMED"
    | "IN_PREPARATION"
    | "READY"
    | "IN_DELIVERY"
    | "DELIVERED"
    | "CANCELLED";
  serviceType: "MEAL_DELIVERY" | "CATERING";
  totalAmount: number;
  totalAmountHt: number;
  vatAmount: number;
  vatRate: number;
  discountAmount: number | null;
  deliveryCost: number | null;
  specialInstructions: string | null;
  paymentStatus?: { id: string; name: string } | null;
  paymentMethod?: { id: string; name: string } | null;
  items: ClientOrderItem[];
  statusHistory?: Array<{ id: string; status: string; createdAt: string }>;
}

export interface UpdateClientProfilePayload {
  firstName?: string;
  lastName?: string;
  phoneDialCode?: string;
  phone?: string;
  email?: string;
  allergenIds?: string[];
}

/** Zone de livraison attribuée automatiquement côté serveur (non envoyée par le client). */
export interface CreateClientAddressPayload {
  label?: string | null;
  address: string;
  postalCode?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isDefault?: boolean;
  isBilling?: boolean;
  displayOrder?: number;
}

export interface CreateClientOrderPayload {
  serviceType: "MEAL_DELIVERY" | "CATERING";
  deliveryDate: string;
  customerAddressId?: string | null;
  specialInstructions?: string | null;
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice: number;
    notes?: string | null;
    /** Formule Switch demandée par le client (sans surcoût). */
    isSwitch?: boolean;
  }>;
}

export const clientApi = {
  // Profil
  async getMe(): Promise<ClientProfile> {
    const { data } = await api.get<{ success: boolean; data: ClientProfile }>("/client/me");
    return data.data;
  },
  async updateMe(payload: UpdateClientProfilePayload): Promise<ClientProfile> {
    const { data } = await api.patch<{ success: boolean; data: ClientProfile }>(
      "/client/me",
      payload,
    );
    return data.data;
  },

  // Pickup
  async getPickupLocation(): Promise<PickupLocation> {
    const { data } = await api.get<{ success: boolean; data: PickupLocation }>(
      "/client/pickup-location",
    );
    return data.data;
  },

  // Adresses
  async listAddresses(): Promise<ClientAddress[]> {
    const { data } = await api.get<{ success: boolean; data: ClientAddress[] }>(
      "/client/addresses",
    );
    return data.data;
  },
  async createAddress(payload: CreateClientAddressPayload): Promise<ClientAddress> {
    const { data } = await api.post<{ success: boolean; data: ClientAddress }>(
      "/client/addresses",
      payload,
    );
    return data.data;
  },
  async updateAddress(
    addressId: string,
    payload: Partial<CreateClientAddressPayload>,
  ): Promise<ClientAddress> {
    const { data } = await api.put<{ success: boolean; data: ClientAddress }>(
      `/client/addresses/${addressId}`,
      payload,
    );
    return data.data;
  },
  async deleteAddress(addressId: string): Promise<void> {
    await api.delete(`/client/addresses/${addressId}`);
  },

  // Menu
  async getMenu(date: string): Promise<ClientMenu | null> {
    const { data } = await api.get<{ success: boolean; data: ClientMenu | null }>(
      `/client/menu?date=${encodeURIComponent(date)}`,
    );
    return data.data;
  },

  async getProductAllergens(
    productId: string,
  ): Promise<Array<{ id: string; name: string; code: string; icon: string | null }>> {
    const { data } = await api.get<{
      success: boolean;
      data: Array<{ id: string; name: string; code: string; icon: string | null }>;
    }>(`/client/products/${productId}/allergens`);
    return data.data ?? [];
  },

  // Commandes
  async listOrders(filters?: {
    status?: string;
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: ClientOrder[]; total?: number; page?: number; limit?: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.deliveryDateFrom) params.set("deliveryDateFrom", filters.deliveryDateFrom);
    if (filters?.deliveryDateTo) params.set("deliveryDateTo", filters.deliveryDateTo);
    if (filters?.page != null) params.set("page", String(filters.page));
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    const query = params.toString();
    const { data } = await api.get<{
      success: boolean;
      data: ClientOrder[];
      total?: number;
      page?: number;
      limit?: number;
    }>(`/client/orders${query ? `?${query}` : ""}`);
    return {
      data: data.data,
      total: data.total,
      page: data.page,
      limit: data.limit,
    };
  },
  async getOrder(orderId: string): Promise<ClientOrder> {
    const { data } = await api.get<{ success: boolean; data: ClientOrder }>(
      `/client/orders/${orderId}`,
    );
    return data.data;
  },
  async createOrder(payload: CreateClientOrderPayload): Promise<ClientOrder> {
    const { data } = await api.post<{ success: boolean; data: ClientOrder }>(
      "/client/orders",
      payload,
    );
    return data.data;
  },
  async cancelOrder(orderId: string): Promise<ClientOrder> {
    const { data } = await api.post<{ success: boolean; data: ClientOrder }>(
      `/client/orders/${orderId}/cancel`,
    );
    return data.data;
  },
  async createCheckout(orderIds: string[]): Promise<{ sessionId: string; url: string }> {
    const { data } = await api.post<{
      success: boolean;
      data: { sessionId: string; url: string };
    }>("/client/checkout", { orderIds });
    return data.data;
  },
  async getCheckoutStatus(sessionId: string): Promise<{
    sessionId: string;
    paymentStatus: string;
    paid: boolean;
    orderIds: string[];
  }> {
    const { data } = await api.get<{
      success: boolean;
      data: {
        sessionId: string;
        paymentStatus: string;
        paid: boolean;
        orderIds: string[];
      };
    }>(`/client/checkout/session/${sessionId}`);
    return data.data;
  },
  async cancelCheckoutSession(sessionId: string): Promise<{ cancelledOrderIds: string[] }> {
    const { data } = await api.post<{
      success: boolean;
      data: { cancelledOrderIds: string[] };
    }>(`/client/checkout/session/${sessionId}/cancel`);
    return data.data;
  },
};
